import * as store from "./stores/AuthStore";
import React from "react";
import { injectIntl } from "react-intl";
import { Reset } from "styled-reset";
import { withStyles } from "@material-ui/core/styles";
import axios from "axios";
import {
  BrowserRouter as Router,
  Redirect,
  Route,
  Switch,
  useLocation,
} from "react-router-dom";
import { inject, observer } from "mobx-react";

import Home from "./views/Home/Home";
import Maintenance from "./views/Home/Maintenance";
import CommonDialog from "./views/CommonComponent/CommonDialog";
import DeviceDataDialogComponent from "./views/Home/DeviceDataDialogComponent";
import Conversation from "./views/Conversation/Conversation";
import Pronunciation from "./views/Pronunciation/Pronunciation";
import Report from "./views/Report/Report";
import Setting from "./views/Setting/Setting";
import UserInfoSetting from "./views/Setting/UserInfoSetting";
import PokoroConnect from "./views/Connect/POKOROConnect";
import WifiConnect from "./views/Connect/WifiConnect";
import AppLanguageSetting from "./views/Setting/AppLanguageSetting";
import SpeakingLanguageSetting from "./views/Setting/SpeakingLanguageSetting";
import ListeningLanguageSetting from "./views/Setting/ListeningLanguageSetting";
import TermsOfUse from "./views/Setting/TermsOfUse";
import NetworkRegionSetting from "./views/Setting/NetworkRegionSetting";
import AccountDelete from "./views/Setting/AccountDelete";
import NewWifiConnect from "./views/Connect/NewWifiConnect";
import PokoroSetting from "./views/Setting/PokoroSetting";
import LanguageSetting from "./views/Setting/LanguageSetting";
import ConnectedPokoroList from "./views/Setting/ConnectedPokoroList";
import DeviceDetail from "./views/Setting/DeviceDetail";
import PenNameEdit from "./views/Setting/PenNameEdit";
import ServiceUpdateAnnouncementComponent from "./views/Home/ServiceUpdateAnnouncementComponent";

import { GlobalProvider } from "./nativebridge/PenStatus";
import SignIn from "./views/SignIn/SignIn";
import SignInInfo from "./views/SignIn/SignInInfo";
import SignInTerms from "./views/SignIn/SignInTerms";
import MemoryConsent from "./views/SignIn/MemoryConsent";
import PushAgree from "./views/Setting/PushAgree"
import GuardianConsent from "./views/SignIn/GuardianConsent";
import SelectDeviceForProfileComponent from "./views/Home/SelectDeviceForProfileComponent";
import ProfileRegistrationPrompt from "./views/SignIn/ProfileRegistrationPrompt";
import PolicyRoutes from "./components/PolicyRoutes";
import GlobalToastHost from "./components/GlobalToastHost";




const style = () => ({
  root: {
    display: "flex",
  },
});

/** 전역 기기/대화 데이터 로딩 다이얼로그를 켤 수 있는 경로만 (그 외에서는 표시하지 않고 로딩 플래그도 내림) */
function isGlobalDeviceDataLoadingPath(pathname) {
  // Home 과 동일하게 exact path ["/", "/home"] 에 해당
  if (pathname === "/" || pathname === "/home") {
    return true;
  }
  if (
    pathname.startsWith("/conversation") ||
    pathname.startsWith("/pronunciation") ||
    pathname.startsWith("/report")
  ) {
    return true;
  }
  return false;
}

const GlobalDeviceDataLoadingDialog = injectIntl(
  inject("loadingStore")(
    observer(function GlobalDeviceDataLoadingDialog(props) {
      const { intl, loadingStore, loginState } = props;
      const location = useLocation();

      React.useEffect(() => {
        if (loginState !== store.State.Authenticated) {
          loadingStore.setIsLoading(false);
          return;
        }
        if (!isGlobalDeviceDataLoadingPath(location.pathname)) {
          loadingStore.setIsLoading(false);
        }
      }, [location.pathname, loadingStore, loginState]);

      const open =
        loginState === store.State.Authenticated &&
        isGlobalDeviceDataLoadingPath(location.pathname) &&
        loadingStore.isLoading;

      return (
        <CommonDialog
          open={open}
          title={intl.formatMessage({ id: "data_device" })}
          subText={intl.formatMessage({ id: "conversation_data" })}
          subTextColor={"#909195"}
          childrenBox={<DeviceDataDialogComponent />}
          action={false}
        />
      );
    })
  )
);

class App extends React.Component {
  componentDidMount() {
    const { loadingStore } = this.props;
    const axiosRequestInterceptors = (config) => {
      const token = localStorage.getItem(store.LocalStorageTokenKey);

      if (token) {
        config.headers["X-Auth-Token"] = token;
      }

      return config;
    };

    const axiosRequestErrorHandler = (error) => {
      return Promise.reject(error);
    };

    const axiosResponseInterceptor = (response) => {
      if (response.status === 403) {
        this.props.authStore.invalidateLogin();
        loadingStore.setIsLoading(false);
      }

      return response;
    };

    const axiosResponseErrorHandler = (error) => {
      if (error.response && error.response.status === 403) {
        this.props.authStore.invalidateLogin();
        loadingStore.setIsLoading(false);
      }

      return Promise.reject(error);
    };

    console.log("========== RGate App componentDidMount ==========");
    axios.interceptors.request.use(
        axiosRequestInterceptors,
        axiosRequestErrorHandler
    );
    axios.interceptors.response.use(
        axiosResponseInterceptor,
        axiosResponseErrorHandler
    );

    this.props.authStore.checkLogin();
  }

  componentDidUpdate(prevProps) {
    const { authStore, loadingStore } = this.props;
    const { loginState, loginUser } = authStore;

    const prevLoginState = prevProps.authStore.loginState;
    const prevProfileExists = prevProps.authStore.loginUser?.profileExists;

    // profile이 없는 유저는 service update 안내로 보내는데,
    // 직전 화면(Home 등)에서 켜진 전역 로딩 다이얼로그가 남아있을 수 있어 강제로 내림.
    if (
      loginState === store.State.Authenticated &&
      loginUser &&
      loginUser.profileExists === false &&
      (prevLoginState !== store.State.Authenticated || prevProfileExists !== false)
    ) {
      loadingStore.setIsLoading(false);
    }
  }

  setMobileOpen(mobileOpen) {
    this.setState({ mobileOpen: mobileOpen });
  }

  render() {
    const { intl, classes } = this.props;
    const { loginState, loginUser } = this.props.authStore;

    // 로그인 체크 중(Pending)에는 어떤 화면도 선마운트하지 않도록 막음.
    // (새로고침 시 Home이 잠깐 뜨며 전역 로딩 다이얼로그가 켜지는 현상 방지)
    if (loginState === store.State.Pending) {
      return <div className={classes.root} />;
    }

    const agreeTerms =
        loginUser.allowTermsOfAge === true &&
        loginUser.allowTermsOfUse === true &&
        loginUser.allowTermsOfPrivacy === true;

    let finalPath = "/";
    if (loginState === store.State.Authenticated) {
      finalPath = localStorage.getItem(store.LocalStorageConnectSkip) ? "/home" : "/pokoroConnect";

      if (!agreeTerms) {
        finalPath = "/signin/terms";
      } else if (!loginUser.profileExists) {
        finalPath = "/signin/service-update-announcement";
      }
    }

    return (
        <div className={classes.root}/*  style={{overflow:'hidden'}} */>
          <GlobalProvider>
            <Router>
              <Reset />
              <GlobalToastHost />
              <Route>
                <Switch>
                  <Route path="/policy" render={() => <PolicyRoutes />} />
                  {loginState !== store.State.Authenticated && (
                      <>
                        <Route exact path="/" component={SignIn} />
                        <Route exact path="/signin" component={SignIn} />
                        {/*<Route exact path="/maintenance" component={Maintenance} />*/}
                        <Redirect to="/" />
                      </>
                  )}
                  {loginState === store.State.Authenticated && (
                      <>
                        <Route exact path="/" render={() => <Redirect to={finalPath} />}/>
                        <Route exact path="/signin/terms" component={SignInTerms} />
                        <Route exact path="/signin/profile-registration-prompt" component={ProfileRegistrationPrompt}/>
                        <Route exact path="/signin/nickname" component={SignInInfo} />
                        <Route exact path="/signin/memory-consent" component={MemoryConsent} />
                        <Route exact path="/signin/guardian-consent" component={GuardianConsent} />
                        <Route exact path="/signin/service-update-announcement" component={ServiceUpdateAnnouncementComponent} />
                        <Route exact path="/signin/select-pokoro-device" component={SelectDeviceForProfileComponent} />
                        <Route exact path={["/", "/home"]} component={Home} />
                        <Route exact path="/wifiConnect" component={WifiConnect} />
                        <Route exact path="/pokoroConnect" component={PokoroConnect} />
                        <Route path="/conversation" component={Conversation} />
                        <Route path="/pronunciation" component={Pronunciation} />
                        <Route path="/report" component={Report} />
                        <Route path="/termsOfUse" component={TermsOfUse} />
                        <Route path="/setting" component={Setting} />
                        <Route path="/appLanguageSetting" component={AppLanguageSetting} />
                        <Route path="/userInfoSetting" component={UserInfoSetting} />
                        <Route path="/speakingLanguageSetting" component={SpeakingLanguageSetting} />
                        <Route path="/listeningLanguageSetting" component={ListeningLanguageSetting} />
                        <Route path="/networkRegionSetting" component={NetworkRegionSetting} />
                        <Route path="/newWifiConnect" component={NewWifiConnect} />
                        <Route path="/accountDelete" component={AccountDelete} />
                        <Route path="/pushAgree" component={PushAgree} />
                        <Route path="/pokoroSetting" component={PokoroSetting} />
                        <Route path="/languageSetting" component={LanguageSetting} />
                        <Route path="/maintenance" component={Maintenance} />
                        <Route path="/deviceList" component={ConnectedPokoroList} />
                        <Route path="/deviceDetail" component={DeviceDetail} />
                        <Route path="/penNameEdit" component={PenNameEdit} />
                      </>
                  )}
                </Switch>
              </Route>
              {/* <Switch>
              <Route exact path="/" component={Setting} />
              <Route exact path="/signin/nickname" component={SignInInfo} />
              <Route exact path="/signin/memory-consent" component={MemoryConsent} />
              <Route exact path="/signin/guardian-consent" component={GuardianConsent} />
              <Route exact path="/home" component={Setting} />
              <Route exact path="/wifiConnect" component={WifiConnect} />
              <Route exact path="/pokoroConnect" component={PokoroConnect} />
              <Route path="/conversation" component={Conversation} />
              <Route path="/pronunciation" component={Pronunciation} />
              <Route path="/report" component={Report} />
              <Route path="/setting" component={Setting} />
              <Route path="/appLanguageSetting" component={AppLanguageSetting} />
              <Route path="/pokoroSetting" component={PokoroSetting} />
              <Route path="/userInfoSetting" component={UserInfoSetting} />
              <Route path="/deviceList" component={ConnectedPokoroList} />
              <Route path="/deviceDetail" component={DeviceDetail} />
              <Route path="/penNameEdit" component={PenNameEdit} />
              <Route path="/languageSetting" component={LanguageSetting} />
              <Route path="/termsOfUse" component={TermsOfUse} />
              <Route path="/accountDelete" component={AccountDelete} />
              <Route path="/networkRegionSetting" component={NetworkRegionSetting}/>
              <Route path="/newWifiConnect" component={NewWifiConnect}/>
              <PolicyRoutes />
            </Switch> */}
              <Route
                render={() => (
                  <GlobalDeviceDataLoadingDialog loginState={loginState} />
                )}
              />
            </Router>
          </GlobalProvider>
        </div>
    );
  }
}

export default withStyles(style)(
    injectIntl(inject("authStore", "loadingStore")(observer(App)))
);
